import { useEffect, useMemo } from 'react';

import { StepProps, useAutoNavigate, useFlowState } from '@betterment/js-runtime';
import { Box, Heading, Text } from '@betterment/samba-react';
import { ErrorBoundary, SambaAutoLink } from '@betterment/shared-components';
import { Formik } from 'formik';
import { useParams } from 'react-router-dom';

import { trackClick } from '../../features/track';
import { LegalAccountTaxationType } from '../../graphql/schema';
import { WorkflowLayout } from '../../Shared/layout/WorkflowLayout';
import { DeferralForm } from '../components/DeferralForm/DeferralForm';
import { DeferralProjectionCard } from '../components/DeferralProjectionCard';
import { EmployerMatchCard } from '../components/EmployerMatchCard';
import { useRetirementContributionRatesQuery } from '../hooks';
import { DeferralSettings, DeferralSettingsFlowState } from '../types';
import { validationSchema } from '../validation';

const DeferralSettingsStep: React.FC<StepProps> = () => {
  const navigate = useAutoNavigate();
  const {
    goToStep,
    updateValues,
    flowState: { returnToPath, values: flowValues },
  } = useFlowState<DeferralSettingsFlowState>();
  const { employerId } = useParams<{ employerId: string }>();
  const { rates, employeeId, loading } = useRetirementContributionRatesQuery(employerId!);
  const employerName = rates?.[0]?.employerName;
  const initialValues = useMemo(() => {
    const priorRate: DeferralSettings = {
      base: {
        traditional: { type: 'percent', value: 0, legalAccountId: '' },
      },
    };

    let bonusSection: DeferralSettings['bonus'];

    if (rates) {
      rates.forEach(rateRow => {
        const accountKey = rateRow.taxationType === LegalAccountTaxationType.Traditional_401K ? 'traditional' : 'roth';

        priorRate.base[accountKey] = {
          type: rateRow.contributionType,
          value: rateRow.value,
          legalAccountId: rateRow.legalAccountId,
        };

        if (rateRow.bonusContributionType !== undefined) {
          if (!bonusSection) {
            bonusSection = {
              traditional: { type: 'percent', value: 0, legalAccountId: '' },
              roth: { type: 'percent', value: 0, legalAccountId: '' },
            };
          }
          bonusSection[accountKey] = {
            type: rateRow.bonusContributionType,
            value: rateRow.bonusValue ?? 0,
            legalAccountId: rateRow.legalAccountId,
          };
        }
      });

      if (bonusSection) {
        priorRate.bonus = bonusSection;
      }
    }

    return priorRate;
  }, [rates]);

  useEffect(() => updateValues({ priorRate: initialValues }), [updateValues, initialValues]);

  return (
    <ErrorBoundary>
      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        validationSchema={validationSchema}
        validateOnChange={true}
        validateOnBlur={true}
        onSubmit={values => {
          goToStep('review', { newRate: values, employeeId, employerName });
        }}
      >
        <WorkflowLayout
          feature="deferral_settings"
          pageTitle="Set your 401(k) contributions"
          workflowTitle="Set your 401(k) contributions"
          size="full"
          onClose={() => navigate(returnToPath)}
        >
          <Box
            css={{
              display: 'flex',
              flexDirection: { base: 'column', md: 'row' },
              gap: { base: 'space.300', md: 'space.400' },
            }}
          >
            <Box css={{ order: { base: 1, md: 2 }, display: { base: 'block', md: 'none' } }}>
              <DeferralProjectionCard />
            </Box>
            <Box css={{ order: { base: 2, md: 1 }, flexBasis: { md: '66.66%' } }}>
              <Box css={{ pb: 'space.300' }}>
                {employerName && (
                  <Text variant="text200" css={{ pb: 'space.50' }}>
                    {employerName}
                  </Text>
                )}
                <Heading variant="pageTitle" css={{ pb: 'space.100' }}>
                  Set your 401(k) contributions.
                </Heading>
                <Text as="p">You can update your contribution settings anytime, they're not locked in.</Text>
                <SambaAutoLink
                  to="https://www.betterment.com/employees/resources/401k-contribution-rate"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackClick({ name: 'HowToSave', flow: 'EditDeferralSetting' })}
                >
                  Learn how traditional and Roth contributions work.
                </SambaAutoLink>
              </Box>
              <DeferralForm loading={loading} priorRate={flowValues?.priorRate} />
            </Box>

            <Box css={{ order: { base: 3, md: 2 }, flexBasis: { md: '33.33%' } }}>
              <Box css={{ display: { base: 'none', md: 'block' } }}>
                <DeferralProjectionCard />
              </Box>
              <EmployerMatchCard />
            </Box>
          </Box>
        </WorkflowLayout>
      </Formik>
    </ErrorBoundary>
  );
};

export default DeferralSettingsStep;
